import gql from 'graphql-tag';

import {RUN_QUEUE_ITEM_FRAGMENT} from '../../graphql/runQueue';

export const DEFAULT_RESOURCE_CONFIG_FRAGMENT = gql`
  fragment DefaultResourceConfigFragment on DefaultResourceConfig {
    id
    createdAt
    updatedAt
    scope {
      __typename
      Type
      ID
    }
    resource
    config
    templateVariables {
      name
      description
      schema
    }
  }
`;

export const FETCH_ENTITY_JOBS = gql`
  query FetchEntityJobs($entityName: String!) {
    entity(name: $entityName) {
      id
      name
      projects(first: 100) {
        edges {
          node {
            id
            name
            artifactType(name: "job") {
              id
              artifactCollections {
                edges {
                  node {
                    id
                    name
                    description
                    artifacts {
                      edges {
                        node {
                          id
                          description
                          labels
                          versionIndex
                          aliases {
                            id
                            alias
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;

export const FETCH_PROJECT_JOBS_PARTIAL = gql`
  query FetchProjectJobsPartial(
    $entityName: String!
    $projectName: String!
    $limit: Int!
    $after: String
  ) {
    project(entityName: $entityName, name: $projectName) {
      id
      name
      artifactType(name: "job") {
        id
        artifactCollections(first: $limit, after: $after) {
          pageInfo {
            hasNextPage
            endCursor
          }
          edges {
            node {
              id
              name
              description
              createdAt
              artifacts {
                edges {
                  node {
                    id
                    description
                    labels
                    versionIndex
                    aliases {
                      id
                      alias
                    }
                    usedBy(first: 1, order: "-createdAt") {
                      totalCount
                      edges {
                        node {
                          id
                          name
                          displayName
                          state
                          createdAt
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;

export const FETCH_JOB_VERSIONS = gql`
  query FetchJobVersions(
    $entityName: String!
    $projectName: String!
    $jobName: String!
  ) {
    project(entityName: $entityName, name: $projectName) {
      id
      artifactCollection(name: $jobName) {
        id
        description
        artifacts {
          edges {
            node {
              id
              createdAt
              createdBy {
                __typename
                ... on User {
                  id
                  name
                  username
                  photoUrl
                }
                ... on Run {
                  id
                  name
                  displayName
                  defaultColorIndex
                }
              }
              aliases {
                id
                alias
              }
            }
          }
        }
      }
    }
  }
`;

export const FETCH_JOB = gql`
  query FetchJob($jobId: ID!) {
    artifactCollection(id: $jobId) {
      id
      name
    }
  }
`;

export const FETCH_JOB_DETAILS = gql`
  query FetchJobDetails($jobId: ID!) {
    artifactCollection(id: $jobId) {
      id
      name
      description
      createdAt
      project {
        id
        access
        name
        entity {
          id
          name
        }
      }
      artifacts {
        edges {
          node {
            id
            description
            labels
            versionIndex
            aliases {
              id
              alias
            }
            createdAt
            createdBy {
              __typename
              ... on User {
                id
                name
                username
                photoUrl
              }
              ... on Run {
                id
                name
                displayName
                defaultColorIndex
              }
            }
            usedBy {
              totalCount
              edges {
                node {
                  id
                  name
                  displayName
                  state
                  stopped
                  createdAt
                  user {
                    id
                    name
                    username
                    photoUrl
                  }
                  config
                  project {
                    id
                    name
                    entity {
                      id
                      name
                    }
                  }
                  defaultColorIndex
                  runInfo {
                    args
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;

export const FETCH_PROJECT_RUNS = gql`
  query FetchProjectRuns($entityName: String!, $projectName: String!) {
    project(entityName: $entityName, name: $projectName) {
      id
      runs {
        edges {
          node {
            id
            name
            displayName
            runInfo {
              git {
                remote
                commit
              }
            }
          }
        }
      }
    }
  }
`;

export const FETCH_PROJECT_LAUNCH_QUEUES = gql`
  query FetchProjectLaunchQueues($entityName: String!, $projectName: String!) {
    project(entityName: $entityName, name: $projectName) {
      id
      name
      entity {
        id
        isTeam
        name
      }
      runQueues {
        id
        name
        access
        defaultResourceConfigID
        prioritizationMode
      }
    }
  }
`;

export const ENTITY_PROJECTS_QUERY = gql`
  query FetchEntityProjects($entityName: String!) {
    entity(name: $entityName) {
      id
      projects(first: 100, order: "-createdAt") {
        edges {
          node {
            id
            name
          }
        }
      }
    }
  }
`;

export const ENTITY_PHOTO = gql`
  query EntityPhoto($entityName: String!) {
    entity(name: $entityName) {
      id
      photoUrl
    }
  }
`;

export const USER_ENTITIES = gql`
  query UserEntities {
    viewer {
      id
      teams {
        edges {
          node {
            id
            name
            photoUrl
            members {
              id
              username
              role
            }
          }
        }
      }
    }
  }
`;

export const VIEWER_DESTINATION_PROJECTS = gql`
  query ViewerDestinationProjects {
    viewer {
      id
      teams {
        edges {
          node {
            id
            name
            projects {
              edges {
                node {
                  id
                  name
                }
              }
            }
          }
        }
      }
    }
  }
`;

export const QUEUE_NAMES = gql`
  query QueueNames($entityName: String) {
    project(name: "model-registry", entityName: $entityName) {
      id
      runQueues {
        id
        name
      }
    }
  }
`;

export const ALL_QUEUES = gql`
  query AllQueues {
    viewer {
      id
      entity
      teams {
        edges {
          node {
            id
            name
            organizationId
            launchProject {
              id
              name
              launchAgents {
                id
                name
                createdAt
                heartbeatAt
                agentStatus
                stopPolling
                runQueues
                hostname
              }
              runQueues {
                id
                createdAt
                createdBy
                updatedAt
                name
                access
                prioritizationMode
                executorInfo
                externalLinks
                defaultResourceConfig {
                  id
                  ...DefaultResourceConfigFragment
                }
                runQueueItems(first: 1000) {
                  edges {
                    node {
                      id
                      ...RunQueueItemFragment
                      error {
                        message
                        stage
                        filePaths
                      }
                      warnings {
                        message
                        stage
                        filePaths
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }

  ${RUN_QUEUE_ITEM_FRAGMENT}
  ${DEFAULT_RESOURCE_CONFIG_FRAGMENT}
`;

export const ENTITY_QUEUES_LITE = gql`
  query EntityQueuesLite($entityName: String) {
    entity(name: $entityName) {
      id
      name
      launchProject {
        id
        name
        launchAgents {
          id
          name
          createdAt
          heartbeatAt
          agentStatus
          stopPolling
          runQueues
          hostname
        }
        runQueues {
          id
          createdAt
          createdBy
          updatedAt
          name
          access
          defaultResourceConfig {
            id
            createdAt
            updatedAt
            scope {
              __typename
              Type
              ID
            }
            resource
            config
            templateVariables {
              name
              description
              schema
            }
          }
          prioritizationMode
          executorInfo
          externalLinks
        }
      }
    }
  }
`;

export const ALL_QUEUES_LITE = gql`
  query AllQueuesLite {
    viewer {
      id
      entity
      teams {
        edges {
          node {
            id
            name
            organizationId
            launchProject {
              id
              name
              launchAgents {
                id
                name
                createdAt
                heartbeatAt
                agentStatus
                stopPolling
                runQueues
                hostname
              }
              runQueues {
                id
                createdAt
                createdBy
                updatedAt
                name
                access
                prioritizationMode
                executorInfo
                externalLinks
                defaultResourceConfig {
                  id
                  createdAt
                  updatedAt
                  scope {
                    __typename
                    Type
                    ID
                  }
                  resource
                  config
                  templateVariables {
                    name
                    description
                    schema
                  }
                }
              }
            }
          }
        }
      }
    }
  }

  ${RUN_QUEUE_ITEM_FRAGMENT}
`;

// Similar to RUNS_STATE_QUERY in src/graphql
export const RUNS_INFORMATION_QUERY = gql`
  query RunsInformation(
    $projectName: String!
    $entityName: String
    $filters: JSONString
  ) {
    project(name: $projectName, entityName: $entityName) {
      id
      runs(filters: $filters) {
        edges {
          node {
            id
            name
            displayName
            state
            createdAt
            heartbeatAt
          }
        }
      }
    }
  }
`;

export const FETCH_CODE_ARTIFACT_FILES = gql`
  query FetchCodeArtifactFiles($artifactID: ID!) {
    artifact(id: $artifactID) {
      id
      files {
        edges {
          node {
            id
            name
            displayName
            mimetype
            sizeBytes
            url
          }
        }
      }
    }
  }
`;

export const FETCH_AGENT_DETAILS = gql`
  query FetchAgentDetails($agentId: ID!) {
    launchAgent(id: $agentId) {
      id
      name
      createdAt
      updatedAt
      heartbeatAt
      runQueues
      hostname
      agentStatus
      stopPolling
      agentConfig
      version
    }
  }
`;

export const ENTITY_AGENTS = gql`
  query EntityAgents($entityName: String!) {
    entity(name: $entityName) {
      id
      name
      launchProject {
        id
        name
        launchAgents {
          id
          name
          createdAt
          heartbeatAt
          agentStatus
          stopPolling
          runQueues
          hostname
        }
      }
    }
  }
`;

export const FETCH_RUN_OUTPUT_LOG = gql`
  query FetchRunOutputLog(
    $entityName: String!
    $projectName: String!
    $runName: String!
  ) {
    project(name: $projectName, entityName: $entityName) {
      id
      run(name: $runName) {
        id
        logLineCount
        outputLogFile: files(first: 1, names: ["output.log"]) {
          edges {
            node {
              id
              name
              md5
              sizeBytes
              directUrl
              url(upload: false)
            }
          }
        }
      }
    }
  }
`;

export const QUEUE_BY_ID = gql`
  query QueueByID($entityName: String!, $queueId: ID!) {
    project(name: "model-registry", entityName: $entityName) {
      id
      name
      launchAgents {
        id
        name
        createdAt
        heartbeatAt
        agentStatus
        stopPolling
        runQueues
        hostname
      }
      runQueue(id: $queueId) {
        id
        createdAt
        name
        defaultResourceConfig {
          id
          createdAt
          resource
          config
          templateVariables {
            name
            description
            schema
          }
        }
        prioritizationMode
        executorInfo
        externalLinks
        runQueueItems(first: 1000) {
          edges {
            node {
              id
              ...RunQueueItemFragment
              error {
                message
                stage
                filePaths
              }
              warnings {
                message
                stage
                filePaths
              }
            }
          }
        }
      }
    }
  }

  ${RUN_QUEUE_ITEM_FRAGMENT}
`;

export const RUN_JOB_QUERY = gql`
  query RunJob($entityName: String!, $projectName: String!, $runName: String!) {
    project(name: $projectName, entityName: $entityName) {
      id
      run(name: $runName) {
        id
        job {
          id
          artifactSequence {
            id
            name
          }
          versionIndex
        }
      }
    }
  }
`;

export const RUN_FILES_WITH_FOLDER_AND_DIRECTURL = gql`
  query RunFilesAndFolderWithDirectURLs(
    $projectName: String!
    $entityName: String
    $runName: String!
    $maxNumFiles: Int!
    $pattern: String
  ) {
    project(name: $projectName, entityName: $entityName) {
      id
      name
      run(name: $runName) {
        id
        name
        projectId
        files(first: $maxNumFiles, pattern: $pattern) {
          edges {
            node {
              id
              name
              directUrl
              sizeBytes
              updatedAt
              artifact {
                id
                digest
              }
            }
          }
        }
      }
    }
  }
`;

export const ACCOUNT_USING_LAUNCH = gql`
  query AccountUsingLaunch($name: String!) {
    organization(name: $name) {
      id
      isLaunchActive
    }
  }
`;
